// Stations, devices and held station actions (plan §9.3–§9.5, §10.2; D-024). Wire format:
// snake_case fields, instants as ISO-8601 UTC strings.
//
// Admin (`devices.manage`):
//   GET  /stations                         stations per branch, their devices, min versions
//   POST /stations                         add a station
//   PATCH /stations/:id                    rename, change profile or printer, archive
//   POST /devices/pairing-codes            branch code + one-time access key (10 min)
//   POST /devices/pairing-codes/:id/cancel the key stops working
//   POST /devices/:id/revoke               revoke a device (its refresh secret dies)
//   PUT  /devices/min-versions             minimum station app version per platform
//   GET  /held-commands, GET /held-commands/:id, GET /held-commands/:id/preview,
//   POST /held-commands/:id/resolve
//   GET  /rejected-commands, GET /rejected-commands/:id   (read-only)
//
// Station:
//   POST /devices/register   (no auth: a valid, unused, unexpired access key)
//   POST /devices/token      (the device refresh secret → a 10-minute device JWT)
//   GET  /devices/config     (device JWT: min version, entitlement token, server time)
//
// The update gate (plan §9.5, lesson 20). Wherever the server says "this station app is too
// old", it carries the same two members, `reason` and `min_app_version`
// (`upgradeNoticeSchema` in @enp/domain/commands):
//   * `/devices/register`, `/devices/token` → HTTP 426 problem+json
//       { status: 426, code: 'upgrade_required', title, reason: 'app_version',
//         min_app_version: '0.2.0', requestId }
//   * `/devices/config` → 200, `upgrade_required: true` and
//       upgrade: { reason: 'app_version', min_app_version: '0.2.0' }
//   * `/commands` → 200, `upgrade_required: true` and
//       upgrade: { reason: 'app_version' | 'schema_version', min_app_version: string | null }
// A station raises its gate from any of them with `upgradeNoticeFrom(body)` (below).
import { z } from 'zod';
import {
  HOLD_REASONS,
  type UpgradeNotice,
  storedCommandResponseSchema,
  upgradeNoticeSchema,
} from '../commands/response.js';
import { flagSchema } from '../commands/envelope.js';
import {
  DEVICE_PLATFORMS,
  appVersionSchema,
  devicePlatformSchema,
  stationPrinterSchema,
  stationProfileSchema,
} from '../devices/index.js';

const instant = z.iso.datetime({ offset: true });
const count = z.int().min(0);
const deviceCode = z.string().regex(/^[A-Z2-9]{3}$/);

/** `:id` path parameter of station, device and held-command routes. */
export const devicesIdParamsSchema = z.object({ id: z.uuid() });

/** Minimum station app version per platform. */
export const minAppVersionsSchema = z.object(
  Object.fromEntries(DEVICE_PLATFORMS.map((p) => [p, appVersionSchema])) as Record<
    (typeof DEVICE_PLATFORMS)[number],
    typeof appVersionSchema
  >,
);

// ---------------------------------------------------------------------------------------
// Stations and devices (Admin)

/** A device serving a station, as the Admin sees it. */
export const deviceSummarySchema = z.object({
  id: z.uuid(),
  station_id: z.uuid(),
  /** 3 characters, never reused in the account (plan §9.4). */
  device_code: deviceCode,
  platform: devicePlatformSchema,
  /** The app version the device last reported, if any. */
  app_version: z.string().nullable(),
  /** True when `app_version` is below the account's minimum for the platform. */
  below_min_version: z.boolean(),
  last_seen_at: instant.nullable(),
  paired_at: instant,
  revoked_at: instant.nullable(),
  revoke_reason: z.string().nullable(),
});
/** A device. */
export type DeviceSummary = z.infer<typeof deviceSummarySchema>;

/** A station and its devices (newest first, revoked ones too). */
export const stationSchema = z.object({
  id: z.uuid(),
  branch_id: z.uuid(),
  name: z.string().min(1),
  profile: stationProfileSchema,
  printer: stationPrinterSchema.nullable(),
  archived_at: instant.nullable(),
  devices: z.array(deviceSummarySchema),
  /** Held station actions from this station's devices, still open. */
  open_holds: count,
});
/** A station. */
export type Station = z.infer<typeof stationSchema>;

/** A branch stations can belong to (replay branches are never listed). */
export const stationBranchSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  /** The branch code a device is paired with. */
  code: z.string().min(1),
});

/** `GET /stations`: the caller's branches, their stations, and the minimum versions. */
export const stationsResponseSchema = z.object({
  branches: z.array(stationBranchSchema),
  stations: z.array(stationSchema),
  min_app_versions: minAppVersionsSchema,
});
/** Response of `GET /stations`. */
export type StationsResponse = z.infer<typeof stationsResponseSchema>;

const stationName = z.string().trim().min(1, 'Give the station a name').max(80);

/** `POST /stations` body. */
export const createStationRequestSchema = z.object({
  branch_id: z.uuid(),
  name: stationName,
  profile: stationProfileSchema,
  printer: stationPrinterSchema.nullable().optional(),
});
/** Body of `POST /stations`. */
export type CreateStationRequest = z.infer<typeof createStationRequestSchema>;

/** `PATCH /stations/:id` body: only what changes. `archived: true` retires the station. */
export const updateStationRequestSchema = z
  .object({
    name: stationName.optional(),
    profile: stationProfileSchema.optional(),
    printer: stationPrinterSchema.nullable().optional(),
    archived: z.boolean().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'Nothing to change' });
/** Body of `PATCH /stations/:id`. */
export type UpdateStationRequest = z.infer<typeof updateStationRequestSchema>;

/** `POST /devices/pairing-codes` body: the station the new device will serve. */
export const pairingCodeRequestSchema = z.object({ station_id: z.uuid() });

/**
 * `POST /devices/pairing-codes`: shown **once**. The key works for one device, for 10
 * minutes; only its hash is stored.
 */
export const pairingCodeResponseSchema = z.object({
  pairing_id: z.uuid(),
  branch_code: z.string().min(1),
  /** Grouped for reading out: `ABCD-EFGH-JKMN`. */
  access_key: z.string().regex(/^[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}$/),
  /** `PAIR:<branch code>:<access key>`, for the QR. */
  qr_payload: z.string().min(1),
  expires_at: instant,
  station: z.object({ id: z.uuid(), name: z.string(), profile: stationProfileSchema }),
  branch: z.object({ id: z.uuid(), name: z.string() }),
});
/** Response of `POST /devices/pairing-codes`. */
export type PairingCodeResponse = z.infer<typeof pairingCodeResponseSchema>;

/**
 * `POST /devices/pairing-codes/:id/cancel`: the key stops working (the Admin closed its
 * dialog without a device). Issuing a new key for a station also cancels its earlier live
 * keys. `expired`: it had already run out. A key a device used answers 409 `pairing_used`.
 */
export const pairingCancelResponseSchema = z.object({
  pairing_id: z.uuid(),
  status: z.enum(['cancelled', 'expired']),
});
/** Response of `POST /devices/pairing-codes/:id/cancel`. */
export type PairingCancelResponse = z.infer<typeof pairingCancelResponseSchema>;

/** `POST /devices/:id/revoke` body. */
export const revokeDeviceRequestSchema = z.object({
  reason: z.string().trim().min(1, 'Say why the device is revoked').max(500),
});

/** `POST /devices/:id/revoke` response: the device, now revoked. */
export const revokeDeviceResponseSchema = deviceSummarySchema;

/** `PUT /devices/min-versions` body: the platforms to change. */
export const updateMinAppVersionsRequestSchema = minAppVersionsSchema
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: 'Nothing to change' });

/** `PUT /devices/min-versions` response: every platform's minimum. */
export const minAppVersionsResponseSchema = z.object({ min_app_versions: minAppVersionsSchema });

// ---------------------------------------------------------------------------------------
// Device side (stations)

/** `POST /devices/register` body (unauthenticated; the access key is the credential). */
export const deviceRegisterRequestSchema = z.object({
  branch_code: z.string().trim().min(1).max(64),
  /** As typed or scanned; case, spaces and hyphens don't matter. */
  access_key: z.string().min(1).max(64),
  platform: devicePlatformSchema,
  app_version: appVersionSchema,
});
/** Body of `POST /devices/register`. */
export type DeviceRegisterRequest = z.infer<typeof deviceRegisterRequestSchema>;

/**
 * `POST /devices/register`. `refresh_secret` is shown once: keep it in the OS secure
 * storage (Keychain / Keystore / safeStorage); the server keeps only its hash.
 */
export const deviceRegisterResponseSchema = z.object({
  device_id: z.uuid(),
  device_code: deviceCode,
  refresh_secret: z.string().min(32),
  account_id: z.uuid(),
  branch_id: z.uuid(),
  station_id: z.uuid(),
  station_name: z.string(),
  profile: stationProfileSchema,
});
/** Response of `POST /devices/register`. */
export type DeviceRegisterResponse = z.infer<typeof deviceRegisterResponseSchema>;

/** `POST /devices/token` body. */
export const deviceTokenRequestSchema = z.object({
  device_id: z.uuid(),
  refresh_secret: z.string().min(1).max(256),
  app_version: appVersionSchema,
});
/** Body of `POST /devices/token`. */
export type DeviceTokenRequest = z.infer<typeof deviceTokenRequestSchema>;

/** `POST /devices/token`: a device JWT (`aud` enp-device, 10 minutes). */
export const deviceTokenResponseSchema = z.object({
  token: z.string().min(1),
  expires_at: instant,
  account_id: z.uuid(),
  branch_id: z.uuid(),
  station_id: z.uuid(),
  device_id: z.uuid(),
  device_code: deviceCode,
});
/** Response of `POST /devices/token`. */
export type DeviceTokenResponse = z.infer<typeof deviceTokenResponseSchema>;

/**
 * `GET /devices/config`: what a station checks on every sync. `server_time` lets the
 * station warn when its clock is off by more than two minutes (plan §9.5).
 */
export const deviceConfigResponseSchema = z.object({
  server_time: instant,
  platform: devicePlatformSchema,
  min_app_version: appVersionSchema,
  /** True when this device's app is below `min_app_version` (it must update). */
  upgrade_required: z.boolean(),
  /** Present exactly when `upgrade_required` is true: the gate to raise. */
  upgrade: upgradeNoticeSchema.optional(),
  entitlement: z.object({
    token: z.string().min(1),
    kid: z.string(),
    expires_at: instant,
    modules: z.array(z.string()),
  }),
  station: z.object({
    id: z.uuid(),
    name: z.string(),
    profile: stationProfileSchema,
    printer: stationPrinterSchema.nullable(),
  }),
  device: z.object({ id: z.uuid(), device_code: deviceCode }),
});
/** Response of `GET /devices/config`. */
export type DeviceConfigResponse = z.infer<typeof deviceConfigResponseSchema>;

/**
 * The problem+json body of a 426 from `/devices/register` or `/devices/token`
 * (`code: 'upgrade_required'`): the station app must update first. `reason` and
 * `min_app_version` are the `upgradeNoticeSchema` members.
 */
export const upgradeRequiredProblemSchema = z.looseObject({
  status: z.literal(426),
  code: z.literal('upgrade_required'),
  title: z.string(),
  reason: z.literal('app_version'),
  min_app_version: z.string(),
});
/** A 426 problem body. */
export type UpgradeRequiredProblem = z.infer<typeof upgradeRequiredProblemSchema>;

/**
 * The update gate in any device answer that carries one — a 426 problem body, a
 * `/devices/config` response or a `/commands` response — else null. One reader for every
 * route, so a station can't miss one (review CR3-1).
 */
export function upgradeNoticeFrom(body: unknown): UpgradeNotice | null {
  if (typeof body !== 'object' || body === null) return null;
  const problem = upgradeRequiredProblemSchema.safeParse(body);
  if (problem.success) {
    return { reason: problem.data.reason, min_app_version: problem.data.min_app_version };
  }
  const nested = upgradeNoticeSchema.safeParse((body as { upgrade?: unknown }).upgrade);
  return nested.success ? nested.data : null;
}

// ---------------------------------------------------------------------------------------
// Held station actions (D-024)

/** Where a held action stands. */
export const HOLD_STATUSES = ['open', 'applied', 'discarded'] as const;
/** One of {@link HOLD_STATUSES}. */
export type HoldStatus = (typeof HOLD_STATUSES)[number];

/** A held station action, as listed. */
export const heldCommandSummarySchema = z.object({
  id: z.uuid(),
  /** Null when the envelope had no usable id. */
  command_id: z.uuid().nullable(),
  /** The command type, when the envelope had one. */
  type: z.string().nullable(),
  /** Whether the command is a physical fact (null when the type is unknown). */
  physical: z.boolean().nullable(),
  reason: z.enum(HOLD_REASONS),
  /** Human-readable detail of why it was held. */
  message: z.string().nullable(),
  status: z.enum(HOLD_STATUSES),
  /** Whether "apply" is possible (a valid envelope whose id is free in this account). */
  applicable: z.boolean(),
  /** The device the command says it came from, when that device is in this account. */
  device: z
    .object({
      id: z.uuid(),
      device_code: deviceCode,
      revoked: z.boolean(),
      station_name: z.string(),
    })
    .nullable(),
  /** The device that uploaded it. */
  uploaded_by: z.object({ id: z.uuid(), device_code: deviceCode, station_name: z.string() }),
  staff: z.object({ id: z.uuid(), display_name: z.string() }).nullable(),
  occurred_at: instant.nullable(),
  received_at: instant,
  resolved_at: instant.nullable(),
  resolved_by: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  resolution_note: z.string().nullable(),
});
/** A held station action. */
export type HeldCommandSummary = z.infer<typeof heldCommandSummarySchema>;

/**
 * Names for the ids a command's payload mentions (`product_id`, `lot_id`, `location_id`),
 * when they exist in this account — so a manager can read what it is about.
 */
export const commandRefsSchema = z.object({
  product: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  lot: z.object({ id: z.uuid(), lot_code: z.string() }).nullable(),
  location: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
/** Names for a command's payload ids. */
export type CommandRefs = z.infer<typeof commandRefsSchema>;

/** A held station action with the command exactly as uploaded and what resolving did. */
export const heldCommandDetailSchema = heldCommandSummarySchema.extend({
  /** The uploaded command, verbatim. */
  raw: z.unknown(),
  refs: commandRefsSchema,
  /** The stored result, once applied. */
  result: storedCommandResponseSchema.nullable(),
});
/** A held station action in full. */
export type HeldCommandDetail = z.infer<typeof heldCommandDetailSchema>;

// ---------------------------------------------------------------------------------------
// Applying a held action: the preview (review UI3A-1). `GET /held-commands/:id/preview`
// runs the command exactly as "apply" would, in a transaction that is rolled back, and
// answers what it would do — so a manager sees the impact before a days-old fact lands.

/** Something to know before applying (each has a plain sentence in the Admin). */
export const HOLD_PREVIEW_WARNINGS = [
  /** A stock level would be below zero after it (`effect`). */
  'below_zero_after',
  /** A count dated after it already covers the stock it moves (`effect`, `count`). */
  'later_count',
  /** It names a lot, product or location this account doesn't have (still can't apply). */
  'unknown_lot',
  'unknown_product',
  'unknown_location',
  /** The location belongs to another branch than the device's station. */
  'location_not_in_branch',
  /** The person who did it lacks the permission it needs. */
  'permission_missing',
  /** The person it names isn't an active staff member here. */
  'unknown_staff',
  /** The device clock ran ahead: it will be dated when it reached the server. */
  'clock_ahead',
  /** A count: a later count already covers some rows, which won't be adjusted (B-035). */
  'count_superseded',
] as const;
/** One of {@link HOLD_PREVIEW_WARNINGS}. */
export type HoldPreviewWarningCode = (typeof HOLD_PREVIEW_WARNINGS)[number];

/** One stock movement applying it would write, and the level it leaves. */
export const holdPreviewEffectSchema = z.object({
  product: z.object({ id: z.uuid(), name: z.string() }),
  lot: z.object({ id: z.uuid(), lot_code: z.string() }).nullable(),
  location: z.object({ id: z.uuid(), name: z.string() }),
  state: z.string(),
  /** Signed decimal. */
  qty_delta: z.string(),
  /** The row's level now, once applied (every movement, whenever dated). */
  level_after: z.string(),
  /** The instant the movement is dated in the ledger. */
  occurred_at: instant,
  /**
   * `count_offset`: not the action's own movement but the adjustment written at a later
   * count that covers its stock (D-055/D-059), so the level after that count stays what was
   * counted; `count` names it (UI8B-3). `movement`: the action's own.
   */
  kind: z.enum(['movement', 'count_offset']).default('movement'),
  count: z
    .object({ id: z.uuid(), number: z.string(), counted_at: instant })
    .nullable()
    .default(null),
});
/** A projected movement. */
export type HoldPreviewEffect = z.infer<typeof holdPreviewEffectSchema>;

/** A warning; `effect` indexes `effects`, `count` names the later count. */
export const holdPreviewWarningSchema = z.object({
  code: z.enum(HOLD_PREVIEW_WARNINGS),
  /** Technical detail (English, for "Technical detail"). */
  message: z.string(),
  effect: z.int().min(0).optional(),
  count: z.object({ id: z.uuid(), number: z.string(), counted_at: instant }).optional(),
});
/** A preview warning. */
export type HoldPreviewWarning = z.infer<typeof holdPreviewWarningSchema>;

/**
 * What applying would do:
 *   * `applied` / `applied_with_flags` — it would be recorded (with notes for review);
 *   * `rejected` — the server would refuse it for good (nothing recorded; discard instead);
 *   * `still_rejected` — a physical fact that still can't be recorded (it stays held);
 *   * `already_recorded` — the command is already in the log (applying just closes it);
 *   * `cannot_apply` — this hold can't be applied (by this person, or at all).
 */
export const HOLD_PREVIEW_OUTCOMES = [
  'applied',
  'applied_with_flags',
  'rejected',
  'still_rejected',
  'already_recorded',
  'cannot_apply',
] as const;

/** `GET /held-commands/:id/preview`. */
export const holdPreviewSchema = z.object({
  hold_id: z.uuid(),
  outcome: z.enum(HOLD_PREVIEW_OUTCOMES),
  /**
   * Why it would be refused or can't be applied: a result reason (`forbidden`,
   * `unknown_lot`…) or an API code (`permission_required`, `hold_not_applicable`,
   * `hold_outside_scope`, `hold_cannot_apply_now`, `command_id_conflict`).
   */
  refusal: z.object({ code: z.string(), message: z.string().nullable() }).nullable(),
  /** When the station says it happened (null when the upload didn't say). */
  occurred_at: instant.nullable(),
  received_at: instant,
  /** The instant it would be dated in the ledger (null when it can't be applied). */
  effective_at: instant.nullable(),
  effects: z.array(holdPreviewEffectSchema),
  warnings: z.array(holdPreviewWarningSchema),
  /** The result's flags as the server would store them (technical detail). */
  flags: z.array(flagSchema),
});
/** Response of `GET /held-commands/:id/preview`. */
export type HoldPreview = z.infer<typeof holdPreviewSchema>;

/** `GET /held-commands` query: open ones (default), resolved ones, or all; newest first. */
export const heldCommandsQuerySchema = z.object({
  status: z.enum(['open', 'resolved', 'all']).default('open'),
});

/** Most held actions one list returns. */
export const HELD_COMMANDS_PAGE_MAX = 200;

/** `GET /held-commands`. */
export const heldCommandsResponseSchema = z.object({
  items: z.array(heldCommandSummarySchema),
  /** Open holds in the caller's scope, whatever the filter. */
  open_count: count,
  /** More matched than `items` holds (the oldest are left out). */
  truncated: z.boolean(),
});
/** Response of `GET /held-commands`. */
export type HeldCommandsResponse = z.infer<typeof heldCommandsResponseSchema>;

/**
 * What a manager corrects in a held delivery (`receipt.record`) before booking it (D-066):
 * the goods are there, so a delivery the server couldn't book as sent — a location outside
 * the station's branch, an unknown product, a lot id another product already has — is held,
 * never refused. `location_id`: where it was put; per line (by the station's `line_id`) the
 * product it really is, and/or `new_lot` (a new lot id, keeping the printed lot code when it
 * is free). The hold keeps what the station sent; the booking uses the corrected copy.
 */
export const receiptCorrectionsSchema = z
  .object({
    location_id: z.uuid().optional(),
    lines: z
      .array(
        z
          .object({
            line_id: z.uuid(),
            product_id: z.uuid().optional(),
            new_lot: z.literal(true).optional(),
          })
          .refine((l) => l.product_id !== undefined || l.new_lot === true, {
            message: 'Give the product, a new lot, or both',
          }),
      )
      .max(500)
      .optional(),
  })
  .refine((c) => c.location_id !== undefined || (c.lines?.length ?? 0) > 0, {
    message: 'Nothing to correct',
  })
  .refine((c) => new Set((c.lines ?? []).map((l) => l.line_id)).size === (c.lines ?? []).length, {
    message: 'Correct each line once',
    path: ['lines'],
  });
/** Corrections to a held delivery. */
export type ReceiptCorrections = z.infer<typeof receiptCorrectionsSchema>;

/** `POST /held-commands/:id/preview`: what applying with these corrections would do. */
export const holdPreviewRequestSchema = z.object({
  corrections: receiptCorrectionsSchema.optional(),
});
/** Body of `POST /held-commands/:id/preview`. */
export type HoldPreviewRequest = z.infer<typeof holdPreviewRequestSchema>;

/**
 * `POST /held-commands/:id/resolve`. `apply` runs the command's handler now (with an audit
 * trail), a held delivery with the manager's `corrections` if any (D-066); `discard` needs
 * a note saying why. Both are final.
 */
export const resolveHeldCommandRequestSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('apply'),
    note: z.string().trim().max(500).optional(),
    corrections: receiptCorrectionsSchema.optional(),
  }),
  z.object({
    action: z.literal('discard'),
    note: z.string().trim().min(1, 'Say why it is discarded').max(500),
  }),
]);
/** Body of `POST /held-commands/:id/resolve`. */
export type ResolveHeldCommandRequest = z.infer<typeof resolveHeldCommandRequestSchema>;

// ---------------------------------------------------------------------------------------
// Rejected station actions (plan §9.3 "rejected commands are kept and shown to the
// station's manager", §10.4). Read-only: a rejection is final and the station already
// shows it as refused; the Admin shows what, who, when and why.
//
//   GET /rejected-commands           newest first, within the caller's branch scope
//   GET /rejected-commands/:id       one, by its command id

/** A station action the server refused for good (a stored `rejected` answer). */
export const rejectedCommandSummarySchema = z.object({
  command_id: z.uuid(),
  type: z.string(),
  /**
   * Why: `invalid_payload`, `unknown_staff`, `forbidden` (the staff member lacks the
   * permission), or a command handler's own reason.
   */
  reason: z.string(),
  /** Human-readable detail from the server, when there is one. */
  message: z.string().nullable(),
  /** The device it was recorded as (the station that sent it). */
  device: z.object({ id: z.uuid(), device_code: deviceCode, station_name: z.string() }),
  /** Who did it, when the staff id is a staff member of this account. */
  staff: z.object({ id: z.uuid(), display_name: z.string() }).nullable(),
  /** The station's clock. */
  occurred_at: instant,
  /** When it reached the server (and was refused). */
  received_at: instant,
});
/** A rejected station action. */
export type RejectedCommandSummary = z.infer<typeof rejectedCommandSummarySchema>;

/** A rejected station action with what the station sent. */
export const rejectedCommandDetailSchema = rejectedCommandSummarySchema.extend({
  schema_version: z.int(),
  app_version: z.string().nullable(),
  device_seq: count,
  /** The command's payload, as sent. */
  payload: z.unknown(),
  refs: commandRefsSchema,
});
/** A rejected station action in full. */
export type RejectedCommandDetail = z.infer<typeof rejectedCommandDetailSchema>;

/** Most rejected actions one list returns. */
export const REJECTED_COMMANDS_PAGE_MAX = 200;

/** `GET /rejected-commands`. */
export const rejectedCommandsResponseSchema = z.object({
  items: z.array(rejectedCommandSummarySchema),
  /** More matched than `items` holds (the oldest are left out). */
  truncated: z.boolean(),
});
/** Response of `GET /rejected-commands`. */
export type RejectedCommandsResponse = z.infer<typeof rejectedCommandsResponseSchema>;
